import { Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { MasterclassCover } from "@/components/learn/masterclass-cover"
import { MasterclassStatusBadge } from "@/components/masterclasses/masterclass-status-badge"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { masterclassPath } from "@/routes"
import type { MasterclassRow } from "@/types/masterclasses"

/**
 * OKS-54 — Single Masterclass card (brand-side).
 *
 * Whole card is an `<a>` (Inertia `<Link>`) wrapping the content per
 * UX Spec §6 a11y (semantic link, not div+onClick).
 *
 * Top banner is the resolved cover image (`:card` variant) when one is
 * attached (OKS-649), else the deterministic `MasterclassSwatch` from
 * `id` (UX Spec §2.1) — `image || swatch` via `<MasterclassCover>`. The
 * swatch colour stays stable across renders / between brand/agency walks.
 */
export interface MasterclassCardProps {
  masterclass: MasterclassRow
}

export function MasterclassCard({ masterclass }: MasterclassCardProps) {
  const { t, i18n } = useTranslation()

  const updatedAt = masterclass.updated_at
    ? new Date(masterclass.updated_at)
    : null
  const updatedFormatted = updatedAt
    ? updatedAt.toLocaleDateString(i18n.language === "en" ? "en-US" : "fr-FR", {
        day: "numeric",
        month: "short",
      })
    : "—"

  const id = masterclass.id ?? 0

  return (
    <Card
      className="group focus-within:ring-ring flex flex-col gap-0 overflow-hidden p-0 transition-shadow focus-within:ring-2 hover:shadow-md"
      data-testid={`masterclass-card-${id}`}
    >
      <Link
        href={masterclassPath(id)}
        className="flex h-full flex-col focus:outline-none"
        aria-label={masterclass.title ?? undefined}
      >
        <MasterclassCover
          id={id}
          coverImageUrl={masterclass.cover_image_url ?? null}
          title={masterclass.title}
          size="card"
          data-testid={`masterclass-card-${id}-cover`}
        />

        <CardContent className="flex flex-col gap-2 p-4">
          <h3
            className="line-clamp-2 text-base font-semibold tracking-tight"
            data-testid={`masterclass-card-${id}-title`}
          >
            {masterclass.title}
          </h3>
          <p className="text-muted-foreground line-clamp-1 text-xs">
            {masterclass.topic}
          </p>
          <p className="text-muted-foreground line-clamp-2 text-sm">
            {masterclass.description}
          </p>
        </CardContent>

        <CardFooter className="flex items-center justify-between gap-2 border-t p-4">
          <MasterclassStatusBadge
            state={masterclass.state}
            data-testid={`masterclass-card-${id}-state`}
          />
          <span className="text-muted-foreground text-xs">
            {t("masterclasses.show.updated_relative", {
              relative: updatedFormatted,
            })}
          </span>
        </CardFooter>
      </Link>
    </Card>
  )
}
